/**
 * Koinonia Farm Theme - Global JavaScript
 * Essential functions and initialization
 */

// Mobile menu toggle with full accessibility support
document.addEventListener('DOMContentLoaded', function() {
  const stickySiteHeader = document.querySelector('.site-header--sticky');
  const mainContent = document.querySelector('#MainContent');

  if (stickySiteHeader) {
    const topBar = stickySiteHeader.querySelector('.top-bar');

    // Measure top bar height and set CSS variable + body padding
    function updateHeaderMetrics() {
      const topBarH = topBar ? topBar.offsetHeight : 0;
      const headerH = stickySiteHeader.offsetHeight;
      stickySiteHeader.style.setProperty('--top-bar-height', '-' + topBarH + 'px');
      document.documentElement.style.setProperty('--header-total-height', headerH + 'px');
      if (mainContent) mainContent.style.paddingTop = headerH + 'px';
    }

    updateHeaderMetrics();
    window.addEventListener('resize', updateHeaderMetrics, { passive: true });

    // Fonts load with font-display: swap (see layout/theme.liquid), so the
    // header can still be rendering in a fallback font when the measurement
    // above runs at DOMContentLoaded. If the swap to the real webfont changes
    // the top-bar's line count/height (e.g. the promo text wraps differently),
    // --header-total-height is left stale and the homepage hero's negative
    // margin-top (component-homepage.css) under/overshoots, letting the fixed
    // header clip the top of the hero. One-shot re-measure once fonts settle —
    // NOT a persistent observer (see the removed-ResizeObserver note below).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateHeaderMetrics);
    }

    // Re-measure only when the top-bar's max-height transition COMPLETES,
    // and only if we settled in the visible state. Mid-transition reads
    // (the old ResizeObserver loop) caused --top-bar-height to oscillate
    // between -9px and -0px and shifted mainContent.paddingTop, which fed
    // back into the scroll handler and flickered top-bar-hidden on/off.
    if (topBar) {
      topBar.addEventListener('transitionend', function (e) {
        if (e.propertyName !== 'max-height') return;
        if (stickySiteHeader.classList.contains('top-bar-hidden')) return;
        updateHeaderMetrics();
      });
    }

    let lastScrollY = window.scrollY;
    const SCROLL_THRESHOLD = 60;
    const SCROLL_HYSTERESIS = 4; // px — ignore scroll noise / clamp echoes

    window.addEventListener(
      'scroll',
      function () {
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollY;

        stickySiteHeader.classList.toggle('is-scrolled', currentScrollY > 8);

        if (currentScrollY < 8) {
          stickySiteHeader.classList.remove('top-bar-hidden');
        } else if (delta > SCROLL_HYSTERESIS && currentScrollY > SCROLL_THRESHOLD) {
          stickySiteHeader.classList.add('top-bar-hidden');
        } else if (delta < -SCROLL_HYSTERESIS) {
          stickySiteHeader.classList.remove('top-bar-hidden');
        }
        // Else: scroll delta is within hysteresis zone — no real direction
        // signal, leave class state alone (prevents flicker from idle scroll
        // events fired by layout shifts and document-height clamps).

        lastScrollY = currentScrollY;
      },
      { passive: true }
    );
  }

  const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
  const headerNav = document.querySelector('.header-nav');
  
  // Desktop dropdown menu enhancement
  function setupDropdowns() {
    const navDropdowns = document.querySelectorAll('.nav-dropdown');
    navDropdowns.forEach(function(dropdown) {
      const details = dropdown.querySelector('.nav-dropdown__details');
      const summary = dropdown.querySelector('summary');
      const menu = dropdown.querySelector('.nav-dropdown__menu');
      
      if (details && summary && menu && window.innerWidth >= 990) {
        let closeTimer = null;
        
        // Function to open dropdown
        function openDropdown() {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
          if (!details.hasAttribute('open')) {
            details.setAttribute('open', '');
          }
        }
        
        // Function to close dropdown with delay
        function closeDropdown() {
          if (closeTimer) {
            clearTimeout(closeTimer);
          }
          closeTimer = setTimeout(function() {
            // Double-check mouse is not over dropdown before closing
            if (!dropdown.matches(':hover') && details.hasAttribute('open')) {
              details.removeAttribute('open');
            }
          }, 150);
        }
        
        // On desktop, open dropdown on hover
        dropdown.addEventListener('mouseenter', function() {
          openDropdown();
          // Clear any pending close
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
        });
        
        // Keep dropdown open when hovering over menu
        menu.addEventListener('mouseenter', function() {
          openDropdown();
          // Clear any pending close
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
        });
        
        // Close dropdown when mouse leaves (with delay)
        dropdown.addEventListener('mouseleave', function(e) {
          // Only close if mouse is not moving to menu
          const relatedTarget = e.relatedTarget;
          if (!relatedTarget || !menu.contains(relatedTarget)) {
            closeDropdown();
          }
        });
        
        menu.addEventListener('mouseleave', function(e) {
          // Only close if mouse is not moving to parent
          const relatedTarget = e.relatedTarget;
          if (!relatedTarget || !dropdown.contains(relatedTarget)) {
            closeDropdown();
          }
        });
        
        // Prevent summary from toggling on click (we use hover).
        // 2026-09-08: this used to preventDefault() on EVERY desktop click.
        // The parent collection link lives inside the summary, so its click
        // bubbled up here and had its navigation cancelled -- the top-level
        // menu items looked clickable and did nothing. Let a click that
        // started on that link through; keep cancelling everything else so
        // the details element does not fight the hover behaviour.
        summary.addEventListener('click', function(event) {
          if (window.innerWidth >= 990) {
            if (event.target.closest('.nav-dropdown__parent-link')) return;
            event.preventDefault();
          }
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', function(event) {
          if (!dropdown.contains(event.target) && details.hasAttribute('open')) {
            if (closeTimer) {
              clearTimeout(closeTimer);
            }
            details.removeAttribute('open');
          }
        });
      }
    });
  }
  
  // Setup dropdowns on load
  setupDropdowns();

  // Detect when an L3 flyout would clip the right edge of the viewport,
  // and flip its positioning to open to the LEFT of the L2 trigger instead.
  function setupL3FlipDetection() {
    if (window.innerWidth < 990) return; // mobile uses nested-list mode, no flip needed
    const triggers = document.querySelectorAll('.nav-dropdown__item--has-children');
    triggers.forEach(function (trigger) {
      const submenu = trigger.querySelector('.nav-dropdown__submenu');
      if (!submenu) return;

      function checkFit() {
        // Reset to right-side default to measure the natural position
        trigger.classList.remove('nav-dropdown__item--flip-left');
        submenu.classList.remove('nav-dropdown__submenu--flip-left');
        // Force-show invisibly to measure
        const prevDisplay = submenu.style.display;
        const prevVisibility = submenu.style.visibility;
        submenu.style.display = 'block';
        submenu.style.visibility = 'hidden';
        const rect = submenu.getBoundingClientRect();
        const wouldOverflow = rect.right > window.innerWidth - 8; // 8px safety
        submenu.style.display = prevDisplay;
        submenu.style.visibility = prevVisibility;
        if (wouldOverflow) {
          trigger.classList.add('nav-dropdown__item--flip-left');
          submenu.classList.add('nav-dropdown__submenu--flip-left');
        }
      }

      // Re-check on each hover (in case viewport changed without a resize event)
      trigger.addEventListener('mouseenter', checkFit);
      trigger.addEventListener('focusin', checkFit);
    });
  }

  setupL3FlipDetection();

  // Re-setup on window resize
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      setupDropdowns();
      setupL3FlipDetection();
    }, 250);
  });
  
  if (mobileMenuToggle && headerNav) {
    const mobileMenuClose = document.querySelector('.mobile-menu-close');
    const mobileMenuOverlay = document.querySelector('.mobile-menu-overlay');
    const body = document.body;
    
    // Function to open menu
    function openMenu() {
      headerNav.classList.add('mobile-nav-open');
      body.classList.add('mobile-menu-open');
      mobileMenuToggle.setAttribute('aria-expanded', 'true');
      mobileMenuToggle.setAttribute('aria-label', 'Close menu');
      // Prevent body scroll when menu is open
      body.style.overflow = 'hidden';
      // Focus first focusable element in menu
      trapFocus(headerNav);
    }
    
    // Function to close menu
    function closeMenu() {
      headerNav.classList.remove('mobile-nav-open');
      body.classList.remove('mobile-menu-open');
      mobileMenuToggle.setAttribute('aria-expanded', 'false');
      mobileMenuToggle.setAttribute('aria-label', 'Open menu');
      // Restore body scroll
      body.style.overflow = '';
      // Return focus to toggle button
      mobileMenuToggle.focus();
    }
    
    // Toggle menu on hamburger click
    mobileMenuToggle.addEventListener('click', function() {
      if (headerNav.classList.contains('mobile-nav-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });
    
    // Close menu on close button click
    if (mobileMenuClose) {
      mobileMenuClose.addEventListener('click', function() {
        closeMenu();
      });
    }
    
    // Close menu when clicking overlay
    if (mobileMenuOverlay) {
      mobileMenuOverlay.addEventListener('click', function() {
        closeMenu();
      });
    }
    
    // Close menu with Escape key
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && headerNav.classList.contains('mobile-nav-open')) {
        closeMenu();
      }
    });
    
    // Close menu when clicking on a regular nav link (not dropdown)
    const navLinks = headerNav.querySelectorAll('.nav-link:not(.nav-link--dropdown)');
    navLinks.forEach(function(link) {
      link.addEventListener('click', function() {
        // Small delay to allow navigation
        setTimeout(function() {
          closeMenu();
        }, 100);
      });
    });
  }
  
  // Basic focus trap for mobile menu
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    firstFocusable.focus();
    
    element.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  }
});

// Utility function for cart updates
function updateCart(updates) {
  return fetch(window.routes.cart_update_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ updates })
  })
  .then(response => response.json())
  .catch(error => {
    console.error('Error:', error);
  });
}

// Utility function for adding to cart
function addToCart(formData) {
  return fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(formData)
  })
  .then(response => response.json())
  .catch(error => {
    console.error('Error:', error);
  });
}

// Format money
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision || 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? (decimal + parts[1]) : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

// Expose global functions
window.theme = {
  updateCart,
  addToCart,
  formatMoney
};
